import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import plusIcon from "@iconify/icons-solar/add-circle-linear";
import copyIcon from "@iconify/icons-solar/copy-linear";
import trashIcon from "@iconify/icons-solar/trash-bin-trash-linear";
import TopBar from "../components/layout/TopBar";
import { useToast } from "../context/ToastContext";
import { useConfirmation } from "../context/ConfirmationContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  choices,
  conditionText,
  fields,
  formula,
  money,
  newConfig,
  operators,
  Rule,
  Config,
  Options,
} from "../components/commission/rules";
import "./CommissionRules.css";
const emptyOptions: Options = { agents: [], sections: [], lotTypes: [] };
const today = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
export default function CommissionRules() {
  const { addToast } = useToast(),
    { confirmAction } = useConfirmation(),
    { user } = useAuth();
  const admin = user?.role === "ADMIN";
  const [view, setView] = useState<"rules" | "test" | "report" | "edit">(
      "rules",
    ),
    [rules, setRules] = useState<Rule[]>([]),
    [options, setOptions] = useState<Options>(emptyOptions),
    [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true),
    [loadError, setLoadError] = useState(""),
    [refresh, setRefresh] = useState(0),
    [search, setSearch] = useState(""),
    [status, setStatus] = useState("all");
  const [edit, setEdit] = useState<Rule | null>(null),
    [ruleName, setRuleName] = useState(""),
    [config, setConfig] = useState<Config>(newConfig),
    [active, setActive] = useState(false),
    [dirty, setDirty] = useState(false),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const saving = useRef(false);
  const [testForm, setTestForm] = useState({
      saleAmount: "150000",
      paymentType: "INSTALLMENT",
      agentId: "",
      sectionId: "",
      lotTypeId: "",
      stage: "INQUIRY",
      saleDate: today(),
    }),
    [result, setResult] = useState<any>(null),
    [testError, setTestError] = useState(""),
    [testing, setTesting] = useState(false);
  const testRevision = useRef(0);
  const [detail, setDetail] = useState<any>(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    Promise.all([
      api.commissionRules.list(),
      api.commissionRules.options(),
      api.commissionRules.agentSummary(),
    ])
      .then(([r, o, a]) => {
        if (cancelled) return;
        setRules(r);
        setOptions(o);
        setReport(a);
        setTestForm((f) => ({
          ...f,
          agentId: f.agentId || o.agents[0]?.id || "",
          sectionId: f.sectionId || o.sections[0]?.id || "",
        }));
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refresh]);
  useEffect(() => {
    const leave = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", leave);
    return () => window.removeEventListener("beforeunload", leave);
  }, [dirty]);
  const invalidateTest = () => {
    testRevision.current++;
    setResult(null);
    setTestError("");
    setTesting(false);
  };
  const change = (next: Config) => {
    setConfig(next);
    setDirty(true);
    invalidateTest();
  };
  const go = async (next: typeof view) => {
    if (saving.current) return;
    if (dirty) {
      const discarded = await confirmAction({
        title: "Discard unsaved rule changes?",
        description:
          "Your edits have not been saved. Leave the builder and discard them?",
        confirmLabel: "Discard changes",
        pendingLabel: "Discarding…",
        action: () => {},
      });
      if (!discarded) return;
    }
    setDirty(false);
    setView(next);
    setDetail(null);
    setError("");
    invalidateTest();
  };
  const start = (rule?: Rule, duplicate = false) => {
    setDetail(null);
    setEdit(duplicate ? null : rule || null);
    setRuleName(rule ? rule.name + (duplicate ? " (copy)" : "") : "");
    setConfig(
      rule && !rule.validationError
        ? JSON.parse(JSON.stringify(rule.rule_json))
        : newConfig(),
    );
    setActive(duplicate ? false : rule?.is_active || false);
    setView("edit");
    setDirty(duplicate);
    setError(
      rule?.validationError
        ? "This saved rule is invalid. Replace its definition before saving."
        : "",
    );
    invalidateTest();
  };
  const persist = async () => {
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    setError("");
    try {
      const body = {
        name: ruleName,
        rule_json: config,
        is_active: active,
        expectedUpdatedAt: edit?.updatedAt,
      };
      if (edit) await api.commissionRules.update(edit.id, body);
      else await api.commissionRules.create(body);
      setDirty(false);
      setView("rules");
      setRefresh((n) => n + 1);
      addToast(
        "success",
        "Commission rule saved",
        active
          ? "It will apply to matching new sales."
          : "The rule is inactive until activated.",
      );
    } catch (e: any) {
      setError(e.message);
      addToast("error", "Rule not saved", e.message);
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };
  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (active) {
      confirmAction({
        title: "Save this active rule?",
        description:
          "This changes commission calculations for new sales and reservations. Existing sale commissions are preserved.",
        confirmLabel: "Save active rule",
        pendingLabel: "Saving…",
        action: async () => {
          if (saving.current) return;
          saving.current = true;
          setBusy(true);
          try {
            const body = {
              name: ruleName,
              rule_json: config,
              is_active: active,
              expectedUpdatedAt: edit?.updatedAt,
            };
            if (edit) await api.commissionRules.update(edit.id, body);
            else await api.commissionRules.create(body);
            setDirty(false);
            setView("rules");
            setRefresh((n) => n + 1);
          } finally {
            saving.current = false;
            setBusy(false);
          }
        },
        successTitle: "Active rule saved",
      });
    } else persist();
  };
  const toggle = (r: Rule) =>
    confirmAction({
      title: r.is_active ? `Deactivate ${r.name}?` : `Activate ${r.name}?`,
      description:
        "The change applies to new sales and map reservations. Recorded sale commissions stay unchanged.",
      confirmLabel: r.is_active ? "Deactivate rule" : "Activate rule",
      pendingLabel: "Updating…",
      successTitle: "Rule status updated",
      action: async () => {
        await api.commissionRules.toggle(r.id, !r.is_active, r.updatedAt);
        setRefresh((n) => n + 1);
      },
    });
  const remove = (r: Rule) =>
    confirmAction({
      title: `Delete ${r.name}?`,
      description:
        "Permanently remove this rule from future calculations. Existing sale commissions and their saved breakdowns are preserved.",
      confirmLabel: "Delete rule",
      successTitle: "Commission rule deleted",
      action: async () => {
        await api.commissionRules.delete(r.id, r.updatedAt);
        setRefresh((n) => n + 1);
      },
    });
  const runTest = async () => {
    const revision = ++testRevision.current;
    setTesting(true);
    setTestError("");
    setResult(null);
    try {
      const data = await api.commissionRules.test({
        ...testForm,
        saleAmount: Number(testForm.saleAmount),
        ...(view === "edit"
          ? { draft: { name: ruleName, rule_json: config } }
          : {}),
      });
      if (revision === testRevision.current) setResult(data);
    } catch (e: any) {
      if (revision === testRevision.current) setTestError(e.message);
    } finally {
      if (revision === testRevision.current) setTesting(false);
    }
  };
  const conditions = config.conditions.all ?? config.conditions.any ?? [],
    mode = config.conditions.all ? "all" : "any",
    params = config.event.params;
  const type =
    params.overridePercentage !== undefined
      ? "override"
      : params.tiers
        ? "tiered"
        : params.commissionPercentage !== undefined
          ? "percentage"
          : "fixed";
  const setParams = (p: Config["event"]["params"]) =>
    change({ ...config, event: { type: "commission", params: p } });
  const visible = rules
    .filter(
      (r) =>
        `${r.name} ${r.rule_json?.description || ""}`
          .toLowerCase()
          .includes(search.toLowerCase()) &&
        (status === "all" ||
          (status === "active" ? r.is_active : !r.is_active)),
    )
    .sort(
      (a, b) => (b.rule_json?.priority || 0) - (a.rule_json?.priority || 0),
    );
  const testPanel = (
    <section className="commission-test" aria-label="Commission simulator">
      <header>
        <h3>{view === "edit" ? "Test this draft" : "Test active rules"}</h3>
        <p>
          {view === "edit"
            ? "Evaluate this draft on its own before saving."
            : "Evaluate all active rules using the same calculation as a new sale."}{" "}
          Testing never creates a sale or payout.
        </p>
      </header>
      <div className="commission-test-fields">
        <label>
          Sale amount (₱)
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={testForm.saleAmount}
            onChange={(e) => {
              setTestForm({ ...testForm, saleAmount: e.target.value });
              invalidateTest();
            }}
          />
        </label>
        <label>
          Agent
          <select
            value={testForm.agentId}
            onChange={(e) => {
              setTestForm({ ...testForm, agentId: e.target.value });
              invalidateTest();
            }}
          >
            <option value="">Select agent</option>
            {options.agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {a.role.toLowerCase()}
              </option>
            ))}
          </select>
        </label>
        <label>
          Payment type
          <select
            value={testForm.paymentType}
            onChange={(e) => {
              setTestForm({ ...testForm, paymentType: e.target.value });
              invalidateTest();
            }}
          >
            <option value="CASH">Cash</option>
            <option value="INSTALLMENT">Installment</option>
          </select>
        </label>
        <label>
          Created through
          <select
            value={testForm.stage}
            onChange={(e) => {
              setTestForm({ ...testForm, stage: e.target.value });
              invalidateTest();
            }}
          >
            <option value="INQUIRY">Sales pipeline</option>
            <option value="RESERVATION">Map reservation</option>
          </select>
        </label>
        <label>
          Section
          <select
            value={testForm.sectionId}
            onChange={(e) => {
              setTestForm({ ...testForm, sectionId: e.target.value });
              invalidateTest();
            }}
          >
            <option value="">Select section</option>
            {options.sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label || s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Lot type
          <select
            value={testForm.lotTypeId}
            onChange={(e) => {
              setTestForm({ ...testForm, lotTypeId: e.target.value });
              invalidateTest();
            }}
          >
            <option value="">No lot type</option>
            {options.lotTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Sale creation date
          <input
            type="date"
            value={testForm.saleDate}
            onChange={(e) => {
              setTestForm({ ...testForm, saleDate: e.target.value });
              invalidateTest();
            }}
          />
        </label>
      </div>
      <button
        type="button"
        className="cr-button primary"
        disabled={testing || !testForm.agentId || !Number(testForm.saleAmount)}
        onClick={runTest}
      >
        {testing ? "Calculating…" : "Calculate commission"}
      </button>
      {testError && (
        <p role="alert" className="cr-error">
          {testError}
        </p>
      )}
      {result && (
        <div className="commission-result" role="status">
          <div>
            <h4>Calculated commission</h4>
            <strong>{money(result.total)}</strong>
            <span>
              {result.rulesApplied} rules applied · {result.rulesEvaluated}{" "}
              evaluated
            </span>
          </div>
          {result.breakdown.length ? (
            <ul>
              {result.breakdown.map((b: any, i: number) => (
                <li key={i}>
                  <span>
                    <strong>{b.ruleName}</strong>
                    <small>{b.description}</small>
                  </span>
                  <b>{money(b.amount)}</b>
                </li>
              ))}
            </ul>
          ) : (
            <p>No commission actions matched this sale.</p>
          )}
        </div>
      )}
    </section>
  );
  return (
    <>
      <TopBar title="Commission Rules" showSearch={false} />
      <main className="commission-workspace">
        <header className="commission-heading">
          <div>
            <h2>Commission rules</h2>
            <p>Define how new sales reward their assigned agent.</p>
          </div>
          {admin && view !== "edit" && (
            <button className="cr-button primary" onClick={() => start()}>
              <Icon icon={plusIcon} width={18} />
              New rule
            </button>
          )}
        </header>
        <div className="commission-policy">
          <strong>Calculated at sale creation.</strong>
          <span>
            Matching rates and bonuses add together. Agent overrides replace
            percentage and tiered rates; fixed and cash bonuses remain. Saved
            sale calculations stay unchanged.
          </span>
        </div>
        <nav className="commission-nav" aria-label="Commission views">
          {(["rules", "test", "report"] as const).map((v) => (
            <button
              key={v}
              aria-current={view === v ? "page" : undefined}
              onClick={() => go(v)}
            >
              {v === "rules"
                ? "Saved rules"
                : v === "test"
                  ? "Test calculation"
                  : "Recorded commissions"}
            </button>
          ))}
          {view === "edit" && <span>{edit ? "Edit rule" : "New rule"}</span>}
        </nav>
        {loadError ? (
          <section className="cr-empty" role="alert">
            <h3>Commission rules could not be loaded</h3>
            <p>{loadError}</p>
            <button
              className="cr-button"
              onClick={() => setRefresh((n) => n + 1)}
            >
              Retry
            </button>
          </section>
        ) : loading ? (
          <div className="cr-loading" role="status">
            Loading commission rules…
          </div>
        ) : (
          <>
            {view === "rules" && (
              <>
                <div className="commission-filters">
                  <label>
                    Search rules
                    <input
                      type="search"
                      placeholder="Rule name or description"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </label>
                  <label>
                    Status
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="all">All rules</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </label>
                  <span>
                    {visible.length} rules ·{" "}
                    {rules.filter((r) => r.is_active).length} active
                  </span>
                </div>
                {visible.length ? (
                  <div
                    className="cr-table-wrap"
                    tabIndex={0}
                    role="region"
                    aria-label="Commission rules"
                  >
                    <table className="cr-table">
                      <thead>
                        <tr>
                          <th>Rule</th>
                          <th>Calculation</th>
                          <th>Applies when</th>
                          <th>Status</th>
                          <th>
                            <span className="cr-sr">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visible.map((r) => {
                          const c = r.rule_json || ({} as Config),
                            p = c?.event?.params,
                            tests = r.validationError
                              ? []
                              : (c?.conditions?.all ??
                                c?.conditions?.any ??
                                []);
                          return (
                            <tr key={r.id}>
                              <td>
                                <button
                                  className="cr-rule-name"
                                  onClick={() =>
                                    admin ? start(r) : setDetail(r)
                                  }
                                >
                                  {r.name}
                                </button>
                                {c?.description && (
                                  <small>{c.description}</small>
                                )}
                                <small>
                                  {c?.effectiveFrom || c?.effectiveTo
                                    ? `${c.effectiveFrom || "Any date"} – ${c.effectiveTo || "No end date"}`
                                    : "No date restriction"}
                                </small>
                                {r.validationError && (
                                  <small className="cr-error">
                                    Invalid definition: {r.validationError}
                                  </small>
                                )}
                              </td>
                              <td>
                                <strong>
                                  {r.validationError
                                    ? "Needs correction"
                                    : formula(c)}
                                </strong>
                                {p?.overridePercentage !== undefined && (
                                  <small>
                                    Priority {c.priority} ·{" "}
                                    {options.agents.find(
                                      (a) => a.id === p.targetAgentId,
                                    )?.name || "Agent unavailable"}
                                  </small>
                                )}
                              </td>
                              <td>
                                <div className="cr-conditions">
                                  {tests.length > 0 ? (
                                    <>
                                      <small>
                                        {c.conditions.all ? "All" : "Any"} of
                                        these conditions
                                      </small>
                                      {tests.map((t, i) => (
                                        <span key={i}>
                                          {conditionText(t, options)}
                                        </span>
                                      ))}
                                    </>
                                  ) : (
                                    <span>All new sales</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span
                                  className={`cr-status ${r.is_active ? "active" : ""}`}
                                >
                                  {r.is_active ? "Active" : "Inactive"}
                                </span>
                                {r.is_active &&
                                  c.effectiveFrom &&
                                  c.effectiveFrom > today() && (
                                    <small>Starts {c.effectiveFrom}</small>
                                  )}
                                {r.is_active &&
                                  c.effectiveTo &&
                                  c.effectiveTo < today() && (
                                    <small>Ended {c.effectiveTo}</small>
                                  )}
                              </td>
                              <td>
                                {admin && (
                                  <div className="cr-row-actions">
                                    <button
                                      className="cr-link"
                                      disabled={
                                        !!r.validationError && !r.is_active
                                      }
                                      onClick={() => toggle(r)}
                                    >
                                      {r.is_active ? "Deactivate" : "Activate"}
                                    </button>
                                    <button
                                      className="cr-icon-button"
                                      aria-label={`Duplicate ${r.name}`}
                                      disabled={!!r.validationError}
                                      onClick={() => start(r, true)}
                                    >
                                      <Icon icon={copyIcon} width={18} />
                                    </button>
                                    <button
                                      className="cr-icon-button danger"
                                      aria-label={`Delete ${r.name}`}
                                      onClick={() => remove(r)}
                                    >
                                      <Icon icon={trashIcon} width={18} />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="cr-empty">
                    <h3>
                      {rules.length
                        ? "No matching rules"
                        : "No commission rules yet"}
                    </h3>
                    <p>
                      {rules.length
                        ? "Change your search or status filter."
                        : "New sales calculate zero commission until an active rule matches."}
                    </p>
                  </div>
                )}
                {detail && (
                  <section className="cr-read-detail">
                    <h3>{detail.name}</h3>
                    <p>{detail.validationError || formula(detail.rule_json)}</p>
                    <button
                      className="cr-button"
                      onClick={() => setDetail(null)}
                    >
                      Close details
                    </button>
                  </section>
                )}
              </>
            )}
            {view === "test" && testPanel}
            {view === "edit" && admin && (
              <div className="commission-editor">
                <form onSubmit={save} className="cr-builder">
                  <header>
                    <h3>{edit ? "Edit rule" : "Create a rule"}</h3>
                    <p>
                      Choose the eligibility conditions and the amount this rule
                      contributes.
                    </p>
                  </header>
                  {error && (
                    <p role="alert" className="cr-error">
                      {error}
                    </p>
                  )}
                  <fieldset disabled={busy}>
                    <label>
                      Rule name
                      <input
                        required
                        maxLength={120}
                        value={ruleName}
                        onChange={(e) => {
                          setRuleName(e.target.value);
                          setDirty(true);
                          invalidateTest();
                        }}
                      />
                    </label>
                    <label>
                      Description
                      <textarea
                        rows={2}
                        maxLength={1000}
                        value={config.description || ""}
                        onChange={(e) =>
                          change({ ...config, description: e.target.value })
                        }
                      />
                    </label>
                    <div className="cr-form-grid">
                      <label>
                        Effective from
                        <input
                          type="date"
                          value={config.effectiveFrom || ""}
                          onChange={(e) =>
                            change({ ...config, effectiveFrom: e.target.value })
                          }
                        />
                      </label>
                      <label>
                        Effective through
                        <input
                          type="date"
                          min={config.effectiveFrom}
                          value={config.effectiveTo || ""}
                          onChange={(e) =>
                            change({ ...config, effectiveTo: e.target.value })
                          }
                        />
                      </label>
                    </div>
                    <section>
                      <h4>Eligibility</h4>
                      <label>
                        Match
                        <select
                          value={mode}
                          onChange={(e) =>
                            change({
                              ...config,
                              conditions: { [e.target.value]: conditions },
                            })
                          }
                        >
                          <option value="all">All conditions</option>
                          <option value="any">Any condition</option>
                        </select>
                      </label>
                      {!conditions.length && (
                        <p>
                          With no conditions, this rule applies to every new
                          sale within its effective dates.
                        </p>
                      )}
                      <div className="cr-condition-list">
                        {conditions.map((c, i) => (
                          <div key={i}>
                            <label>
                              <span className="cr-sr">
                                Condition {i + 1} field
                              </span>
                              <select
                                value={c.fact}
                                onChange={(e) => {
                                  const fact = e.target.value;
                                  change({
                                    ...config,
                                    conditions: {
                                      [mode]: conditions.map((v, n) =>
                                        n === i
                                          ? {
                                              fact,
                                              operator: "equal",
                                              value:
                                                fact === "saleAmount"
                                                  ? 0
                                                  : choices(fact, options)[0]
                                                      ?.id || "",
                                            }
                                          : v,
                                      ),
                                    },
                                  });
                                }}
                              >
                                {Object.entries(fields).map(([v, l]) => (
                                  <option key={v} value={v}>
                                    {l}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label>
                              <span className="cr-sr">
                                Condition {i + 1} operator
                              </span>
                              <select
                                value={c.operator}
                                onChange={(e) =>
                                  change({
                                    ...config,
                                    conditions: {
                                      [mode]: conditions.map((v, n) =>
                                        n === i
                                          ? { ...v, operator: e.target.value }
                                          : v,
                                      ),
                                    },
                                  })
                                }
                              >
                                {Object.entries(operators)
                                  .filter(
                                    ([op]) =>
                                      c.fact === "saleAmount" ||
                                      ["equal", "notEqual"].includes(op),
                                  )
                                  .map(([v, l]) => (
                                    <option key={v} value={v}>
                                      {l}
                                    </option>
                                  ))}
                              </select>
                            </label>
                            <label>
                              <span className="cr-sr">
                                Condition {i + 1} value
                              </span>
                              {c.fact === "saleAmount" ? (
                                <input
                                  required
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={c.value}
                                  onChange={(e) =>
                                    change({
                                      ...config,
                                      conditions: {
                                        [mode]: conditions.map((v, n) =>
                                          n === i
                                            ? {
                                                ...v,
                                                value: Number(e.target.value),
                                              }
                                            : v,
                                        ),
                                      },
                                    })
                                  }
                                />
                              ) : (
                                <select
                                  required
                                  value={c.value}
                                  onChange={(e) =>
                                    change({
                                      ...config,
                                      conditions: {
                                        [mode]: conditions.map((v, n) =>
                                          n === i
                                            ? { ...v, value: e.target.value }
                                            : v,
                                        ),
                                      },
                                    })
                                  }
                                >
                                  <option value="">Select value</option>
                                  {choices(c.fact, options).map((o) => (
                                    <option key={o.id} value={o.id}>
                                      {o.name}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </label>
                            <button
                              type="button"
                              className="cr-icon-button"
                              aria-label={`Remove condition ${i + 1}`}
                              onClick={() =>
                                change({
                                  ...config,
                                  conditions: {
                                    [mode]: conditions.filter(
                                      (_, n) => n !== i,
                                    ),
                                  },
                                })
                              }
                            >
                              <Icon icon={trashIcon} width={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        className="cr-link"
                        type="button"
                        disabled={conditions.length >= 20}
                        onClick={() =>
                          change({
                            ...config,
                            conditions: {
                              [mode]: [
                                ...conditions,
                                {
                                  fact: "agentRole",
                                  operator: "equal",
                                  value: "AGENT",
                                },
                              ],
                            },
                          })
                        }
                      >
                        Add condition
                      </button>
                    </section>
                    <section>
                      <h4>Commission calculation</h4>
                      <label>
                        Calculation type
                        <select
                          value={type}
                          onChange={(e) =>
                            setParams(newConfig(e.target.value).event.params)
                          }
                        >
                          <option value="percentage">Percentage of sale</option>
                          <option value="fixed">Fixed amount</option>
                          <option value="tiered">Whole-sale tiered rate</option>
                          <option value="override">Agent rate override</option>
                        </select>
                      </label>
                      {type !== "tiered" && (
                        <label>
                          {type === "fixed"
                            ? "Fixed commission (₱)"
                            : "Rate (%)"}
                          <input
                            required
                            type="number"
                            min="0"
                            max={type === "fixed" ? 1e12 : 100}
                            step="0.01"
                            value={
                              type === "fixed"
                                ? params.fixedBonus
                                : type === "override"
                                  ? params.overridePercentage
                                  : params.commissionPercentage
                            }
                            onChange={(e) =>
                              setParams({
                                ...params,
                                [type === "fixed"
                                  ? "fixedBonus"
                                  : type === "override"
                                    ? "overridePercentage"
                                    : "commissionPercentage"]: Number(
                                  e.target.value,
                                ),
                              })
                            }
                          />
                        </label>
                      )}
                      {type === "override" && (
                        <>
                          <label>
                            Target agent
                            <select
                              required
                              value={params.targetAgentId || ""}
                              onChange={(e) =>
                                setParams({
                                  ...params,
                                  targetAgentId: e.target.value,
                                })
                              }
                            >
                              <option value="">Select agent</option>
                              {options.agents.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Override priority
                            <input
                              required
                              type="number"
                              min="0"
                              max="1000"
                              step="1"
                              value={config.priority}
                              onChange={(e) =>
                                change({
                                  ...config,
                                  priority: Number(e.target.value),
                                })
                              }
                            />
                          </label>
                          <p>
                            The highest-priority matching override replaces all
                            matching percentage and tiered rates. Fixed and cash
                            bonuses still apply. Matching overrides must have
                            different priorities.
                          </p>
                        </>
                      )}
                      {type === "tiered" && (
                        <>
                          <p>
                            The matching tier’s rate applies to the whole sale.
                            Minimum is inclusive, maximum exclusive. A maximum
                            of 0 means no upper limit.
                          </p>
                          {params.tiers?.map((t, i) => (
                            <div className="cr-tier" key={i}>
                              {(["min", "max", "rate"] as const).map((k) => (
                                <label key={k}>
                                  {k === "min"
                                    ? "Minimum (₱)"
                                    : k === "max"
                                      ? "Maximum (₱)"
                                      : "Rate (%)"}
                                  <input
                                    required
                                    type="number"
                                    min="0"
                                    max={k === "rate" ? 100 : 1e12}
                                    step="0.01"
                                    value={t[k]}
                                    onChange={(e) =>
                                      setParams({
                                        ...params,
                                        tiers: params.tiers!.map((v, n) =>
                                          n === i
                                            ? {
                                                ...v,
                                                [k]: Number(e.target.value),
                                              }
                                            : v,
                                        ),
                                      })
                                    }
                                  />
                                </label>
                              ))}
                              <button
                                type="button"
                                className="cr-icon-button"
                                aria-label={`Remove tier ${i + 1}`}
                                onClick={() =>
                                  setParams({
                                    ...params,
                                    tiers: params.tiers!.filter(
                                      (_, n) => n !== i,
                                    ),
                                  })
                                }
                              >
                                <Icon icon={trashIcon} width={18} />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="cr-link"
                            disabled={(params.tiers?.length || 0) >= 20}
                            onClick={() => {
                              const tiers = [...(params.tiers || [])];
                              const last = tiers[tiers.length - 1];
                              const min =
                                last?.max || (last?.min || 0) + 100000;
                              if (last?.max === 0)
                                tiers[tiers.length - 1] = { ...last, max: min };
                              setParams({
                                ...params,
                                tiers: [
                                  ...tiers,
                                  {
                                    min: tiers.length ? min : 0,
                                    max: 0,
                                    rate: 5,
                                  },
                                ],
                              });
                            }}
                          >
                            Add tier
                          </button>
                        </>
                      )}
                      {type !== "fixed" && (
                        <label>
                          Additional fixed bonus (₱, optional)
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={params.fixedBonus ?? ""}
                            onChange={(e) => {
                              const p = { ...params };
                              if (e.target.value === "") delete p.fixedBonus;
                              else p.fixedBonus = Number(e.target.value);
                              setParams(p);
                            }}
                          />
                        </label>
                      )}
                      <label>
                        Additional cash-sale bonus (₱, optional)
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={params.spotCashBonus ?? ""}
                          onChange={(e) => {
                            const p = { ...params };
                            if (e.target.value === "") delete p.spotCashBonus;
                            else p.spotCashBonus = Number(e.target.value);
                            setParams(p);
                          }}
                        />
                      </label>
                    </section>
                    <label className="cr-check">
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={(e) => {
                          setActive(e.target.checked);
                          setDirty(true);
                        }}
                      />
                      Active for matching new sales
                    </label>
                  </fieldset>
                  <footer>
                    <button
                      type="button"
                      className="cr-button"
                      disabled={busy}
                      onClick={() => go("rules")}
                    >
                      Cancel
                    </button>
                    <button className="cr-button primary" disabled={busy}>
                      {busy ? "Saving…" : "Save rule"}
                    </button>
                  </footer>
                </form>
                {testPanel}
              </div>
            )}
            {view === "report" && report && (
              <section className="cr-report">
                <header>
                  <h3>Commissions recorded on sales</h3>
                  <p>
                    Amounts belong to the sale’s assigned agent. This is a
                    commission register, not a payout ledger.
                  </p>
                </header>
                <dl>
                  <div>
                    <dt>Completed sales</dt>
                    <dd>{money(report.totals.completedCommission)}</dd>
                  </div>
                  <div>
                    <dt>Open pipeline</dt>
                    <dd>{money(report.totals.pipelineCommission)}</dd>
                  </div>
                  <div>
                    <dt>Non-cancelled sales</dt>
                    <dd>{report.totals.totalSales}</dd>
                  </div>
                </dl>
                {report.totals.legacySales > 0 && (
                  <p className="cr-note">
                    {report.totals.legacySales} historical sales have no saved
                    rule breakdown. Their existing commission amounts are
                    preserved.
                  </p>
                )}
                {report.summary.length ? (
                  <div
                    className="cr-table-wrap"
                    tabIndex={0}
                    role="region"
                    aria-label="Recorded commissions by agent"
                  >
                    <table className="cr-table">
                      <thead>
                        <tr>
                          <th>Agent</th>
                          <th>Sales</th>
                          <th className="numeric">Completed</th>
                          <th className="numeric">Open pipeline</th>
                          <th className="numeric">Total recorded</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.summary.map((a: any) => (
                          <React.Fragment key={a.agent.id}>
                            <tr>
                              <td>
                                <button
                                  className="cr-rule-name"
                                  onClick={() =>
                                    setDetail(
                                      detail?.agent?.id === a.agent.id
                                        ? null
                                        : a,
                                    )
                                  }
                                >
                                  {a.agent.name}
                                </button>
                                <small>{a.agent.role.toLowerCase()}</small>
                              </td>
                              <td>{a.totalSales}</td>
                              <td className="numeric">
                                {money(a.completedCommission)}
                              </td>
                              <td className="numeric">
                                {money(a.pipelineCommission)}
                              </td>
                              <td className="numeric">
                                <strong>{money(a.totalCommission)}</strong>
                              </td>
                            </tr>
                            {detail?.agent?.id === a.agent.id && (
                              <tr>
                                <td colSpan={5}>
                                  <div className="cr-sale-history">
                                    {a.sales.map((s: any) => (
                                      <details key={s.id}>
                                        <summary>
                                          Lot {s.lot} · {s.client} ·{" "}
                                          {money(s.commission)}
                                        </summary>
                                        <p>
                                          {s.stage
                                            .toLowerCase()
                                            .replaceAll("_", " ")}{" "}
                                          · Created{" "}
                                          {new Date(s.date).toLocaleDateString(
                                            "en-PH",
                                          )}
                                        </p>
                                        {s.snapshot ? (
                                          <ul>
                                            {s.snapshot.breakdown.map(
                                              (b: any, i: number) => (
                                                <li key={i}>
                                                  {b.ruleName} · {b.description}{" "}
                                                  — {money(b.amount)}
                                                </li>
                                              ),
                                            )}
                                          </ul>
                                        ) : (
                                          <p>
                                            Historical amount. No calculation
                                            snapshot was recorded.
                                          </p>
                                        )}
                                      </details>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="cr-empty">No recorded sales yet.</div>
                )}
              </section>
            )}
          </>
        )}
      </main>
    </>
  );
}
